// steps/BauhofStep.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Box, CircularProgress, Typography, Card, CardActionArea, CardContent } from "@mui/material";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useAllowScroll } from "../AllowScrollContext";
import NewBauhofSelector from "../NewBauhofSelector/NewBauhofSelector";
import ApiService from "@/api/ApiService";
import { Article, MaterialOrder } from "@/components/planning/Project";

type BauhofPlant = {
  id: number;
  name: string;
  // evtl. weitere Felder, je nach API
};

export default function BauhofStep({
  projectId,
  order,
  onBack,
  onSelect,
}: {
  projectId: number;
  order: MaterialOrder;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(true);
  }, [setAllowScroll]);

  const bauhofQuery = useQuery<BauhofPlant[]>({
    queryKey: ["getBauhofArticles", projectId],
    queryFn: ApiService.getBauhofArticles(projectId),
  });

  const [selectedBauhofId, setSelectedBauhofId] = useState<number | null>(null);
  const [autoSelected, setAutoSelected] = useState(false);

  const bauhoefe = bauhofQuery.data ?? [];

  // Wenn es nur einen Bauhof gibt, automatisch in den Selector springen – aber nur einmal
  useEffect(() => {
    if (!autoSelected && selectedBauhofId === null && bauhoefe.length === 1) {
      setSelectedBauhofId(bauhoefe[0].id);
      setAutoSelected(true);
    }
  }, [bauhoefe, selectedBauhofId, autoSelected]);

  const handleSelectFromBauhof = (selected: [Article, number][]) => {
    if (!selected || selected.length === 0) return;
    const [main, ...rest] = selected;

    const patch: Partial<MaterialOrder> = {
      amount: main[1],
      article: main[0],
      // bestehende AdditionalArticles als gelöscht markieren, dann neue anhängen
      additionalArticles: (order.additionalArticles ?? [])
        .map((x) => ({ ...x, deleted: true }))
        .concat(
          rest.map(([article, amount]) => ({
            id: 0,
            article,
            amount,
            deleted: false,
          })),
        ),
      position: null,
      firstUnload: null,
      transportInformation: [],
      manualDAP: false,
    };

    onSelect(patch);
  };

  const selectedMap = useMemo(() => {
    return Object.fromEntries(
      [[order.article?.id, order.amount]]
        .concat((order.additionalArticles ?? []).map((x) => [x.deleted ? 0 : x.article?.id, x.amount]))
        .filter(([id]) => !!id),
    );
  }, [order.article?.id, order.amount, order.additionalArticles]);

  const handleBack = () => {
    if (selectedBauhofId !== null) {
      const singleAutoSelectedBauhof = autoSelected && bauhoefe.length === 1 && selectedBauhofId === bauhoefe[0].id;

      if (singleAutoSelectedBauhof) {
        // User hat die Liste nie gesehen → direkt zurück zum vorherigen Step
        onBack();
      } else {
        // User hat die Liste gesehen und bewusst gewählt → zurück zur Liste
        setSelectedBauhofId(null);
      }
    } else {
      // wir sind in der Liste → zurück zum vorherigen Wizard-Step
      onBack();
    }
  };

  if (bauhofQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (bauhofQuery.isError) {
    return <Typography>Fehler beim Laden der Bauhöfe</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 18 }}>Bauhof</Typography>
      </Box>

      {selectedBauhofId === null ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {bauhoefe.map((b) => (
            <Box key={b.id} sx={{ mb: 2 }}>
              <Card variant="outlined">
                <CardActionArea onClick={() => setSelectedBauhofId(b.id)}>
                  <CardContent
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Typography sx={{ fontWeight: "bold" }}>{b.name}</Typography>
                    </Box>
                    <ChevronRightIcon />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}

          {bauhoefe.length === 0 ? <Typography>Keine Bauhöfe gefunden</Typography> : null}
        </Box>
      ) : (
        <Box>
          <NewBauhofSelector
            selected={selectedMap}
            onSelect={handleSelectFromBauhof}
            abort={handleBack}
            projectId={projectId}
            plant={selectedBauhofId}
          />
        </Box>
      )}
    </Box>
  );
}
